// WARNING: This is pure AI slop :(

import { KeyboardKeyEnum } from '@/React/Shared/Type/KeyboardKeyEnum'
import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function isVisible(el: HTMLElement): boolean {
    if (
        el.offsetWidth > 0 ||
        el.offsetHeight > 0 ||
        el.getClientRects().length > 0
    ) {
        return true
    }

    if (el.offsetParent !== null) {
        return true
    }

    if (typeof window !== 'undefined' && window.getComputedStyle) {
        const style = window.getComputedStyle(el)
        return style.display !== 'none' && style.visibility !== 'hidden'
    }

    return true
}

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
    isActive: boolean,
    onClose?: () => void,
) {
    const containerRef = useRef<T | null>(null)
    const previousFocusRef = useRef<HTMLElement | null>(null)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
        if (!isActive || typeof document === 'undefined') {
            return
        }

        // Only record previously active element when transitioning to active
        previousFocusRef.current = document.activeElement as HTMLElement | null

        const focusFirst = () => {
            const container = containerRef.current

            if (!container) {
                return
            }

            const autoFocusElement =
                container.querySelector<HTMLElement>('[autofocus]')

            if (autoFocusElement && isVisible(autoFocusElement)) {
                autoFocusElement.focus()
                return
            }

            const focusableElements = Array.from(
                container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
            ).filter(isVisible)

            if (focusableElements.length > 0) {
                focusableElements[0].focus()
            } else {
                container.focus()
            }
        }

        const timer = setTimeout(focusFirst, 10)

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === KeyboardKeyEnum.Escape) {
                if (onCloseRef.current) {
                    event.preventDefault()
                    event.stopPropagation()
                    onCloseRef.current()
                }
                return
            }

            if (event.key === KeyboardKeyEnum.Tab) {
                const container = containerRef.current

                if (!container) {
                    return
                }

                const focusableElements = Array.from(
                    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
                ).filter(isVisible)

                if (focusableElements.length === 0) {
                    event.preventDefault()
                    return
                }

                const firstElement = focusableElements[0]
                const lastElement =
                    focusableElements[focusableElements.length - 1]

                if (event.shiftKey) {
                    if (
                        document.activeElement === firstElement ||
                        !container.contains(document.activeElement)
                    ) {
                        event.preventDefault()
                        lastElement.focus()
                    }
                } else {
                    if (
                        document.activeElement === lastElement ||
                        !container.contains(document.activeElement)
                    ) {
                        event.preventDefault()
                        firstElement.focus()
                    }
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            clearTimeout(timer)
            window.removeEventListener('keydown', handleKeyDown)

            if (
                previousFocusRef.current &&
                typeof document !== 'undefined' &&
                document.contains(previousFocusRef.current)
            ) {
                previousFocusRef.current.focus()
            }
        }
    }, [isActive])

    return containerRef
}
